import { REVENUE, PIPELINE_STAGES } from "./phase1Data";
import { TrendingUp, Users, FlaskConical, Building2, DollarSign, CalendarClock, RefreshCw } from "lucide-react";
import { SectionHeader } from "./ui";
import { LiveBadge } from "./ui-badges";

const fmt$ = (v) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(v);

const STAGE_LABEL = Object.fromEntries(
  PIPELINE_STAGES.map((s) => [s.key, s.label])
);
const STAGE_TONE = {
  lead: "bg-slate-300",
  contacted: "bg-sky-400",
  demo_scheduled: "bg-violet-400",
  trial: "bg-amber-400",
  proposal_sent: "bg-indigo-400",
  negotiation: "bg-fuchsia-400",
};

export const RevenueDashboard = () => {
  const maxMrr = Math.max(...REVENUE.mrrHistory.map((m) => m.value), 1);
  const maxStageValue = Math.max(...REVENUE.pipelineByStage.map((s) => s.value), 1);

  return (
    <div data-testid="revenue-dashboard" className="space-y-6">
      <div>
        <div className="flex items-center justify-between gap-3">
          <h1 className="text-2xl font-semibold text-slate-900 tracking-tight">
            Revenue
          </h1>
          <LiveBadge live={false} />
        </div>
        <p className="text-sm text-slate-500 mt-1">
          Blue Harbor Labs growth snapshot — pipeline value, MRR, ARR, and renewal cadence.
        </p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
        <KpiCard
          tone="sky"
          icon={Users}
          label="Prospects"
          value={REVENUE.prospects.count}
          sub={REVENUE.prospects.trend}
          testId="kpi-prospects"
        />
        <KpiCard
          tone="amber"
          icon={FlaskConical}
          label="Trials"
          value={REVENUE.trials.count}
          sub={REVENUE.trials.trend}
          testId="kpi-trials"
        />
        <KpiCard
          tone="emerald"
          icon={Building2}
          label="Active customers"
          value={REVENUE.activeCustomers.count}
          sub={REVENUE.activeCustomers.trend}
          testId="kpi-active"
        />
        <KpiCard
          tone="indigo"
          icon={DollarSign}
          label="Monthly Recurring Revenue"
          value={fmt$(REVENUE.mrr.value)}
          sub={REVENUE.mrr.trend}
          testId="kpi-mrr"
        />
        <KpiCard
          tone="violet"
          icon={TrendingUp}
          label="Annual Recurring Revenue"
          value={fmt$(REVENUE.arr.value)}
          sub={REVENUE.arr.trend}
          testId="kpi-arr"
        />
        <KpiCard
          tone="rose"
          icon={CalendarClock}
          label="Renewals due (next 90d)"
          value={REVENUE.renewalsDue.count}
          sub={REVENUE.renewalsDue.trend}
          testId="kpi-renewals"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        <div className="lg:col-span-3 rounded-xl border border-slate-200 bg-white p-5">
          <SectionHeader title="MRR — last 6 months" subtitle="Recurring revenue trajectory" />
          <div className="flex items-end justify-between gap-2 h-44 mt-3" data-testid="mrr-chart">
            {REVENUE.mrrHistory.map((m) => {
              const h = Math.max((m.value / maxMrr) * 100, 4);
              return (
                <div key={m.month} className="flex-1 flex flex-col items-center gap-2">
                  <div className="text-[10px] text-slate-500 tabular-nums">
                    {m.value === 0 ? "—" : fmt$(m.value)}
                  </div>
                  <div className="w-full flex justify-center">
                    <div
                      className="w-8 rounded-t-md bg-gradient-to-t from-[#0d3b66] to-[#1f5a96]"
                      style={{ height: `${h}%`, minHeight: 4 }}
                    />
                  </div>
                  <div className="text-[11px] font-medium text-slate-600">{m.month}</div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="lg:col-span-2 rounded-xl border border-slate-200 bg-white p-5">
          <SectionHeader
            title="Pipeline by stage"
            subtitle="Weighted projection"
            right={
              <div className="text-right">
                <div className="text-[11px] uppercase tracking-wide text-slate-500">
                  Weighted
                </div>
                <div className="text-sm font-semibold text-slate-900 tabular-nums">
                  {fmt$(REVENUE.weightedPipeline)}
                </div>
              </div>
            }
          />
          <ul className="space-y-2.5 mt-3" data-testid="pipeline-stage-bars">
            {REVENUE.pipelineByStage.map((s) => {
              const pct = (s.value / maxStageValue) * 100;
              return (
                <li key={s.stage}>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-slate-700 font-medium">
                      {STAGE_LABEL[s.stage]}
                    </span>
                    <span className="text-slate-500 tabular-nums">
                      {s.count} · {fmt$(s.value)}
                    </span>
                  </div>
                  <div className="mt-1 h-2 w-full rounded-full bg-slate-100 overflow-hidden">
                    <div
                      className={`h-full ${STAGE_TONE[s.stage] || "bg-slate-400"} transition-all`}
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      </div>

      <div className="rounded-xl border border-amber-200 bg-amber-50/50 p-4 flex items-start gap-3">
        <RefreshCw className="h-4 w-4 text-amber-700 mt-0.5" />
        <div className="text-xs text-amber-900 leading-relaxed">
          <span className="font-semibold">Mock revenue data.</span> Values are
          illustrative only — Stripe is not connected and no payment data is read or written.
        </div>
      </div>
    </div>
  );
};

const TONE_TILE = {
  sky: "from-sky-50 ring-sky-200 text-sky-700",
  amber: "from-amber-50 ring-amber-200 text-amber-700",
  emerald: "from-emerald-50 ring-emerald-200 text-emerald-700",
  indigo: "from-indigo-50 ring-indigo-200 text-indigo-700",
  violet: "from-violet-50 ring-violet-200 text-violet-700",
  rose: "from-rose-50 ring-rose-200 text-rose-700",
};

const KpiCard = ({ tone, icon: Icon, label, value, sub, testId }) => (
  <div
    data-testid={testId}
    className={`rounded-xl border bg-white p-5 ring-1 ring-inset bg-gradient-to-br to-white ${TONE_TILE[tone]}`}
  >
    <div className="flex items-start justify-between">
      <div className={`flex h-9 w-9 items-center justify-center rounded-lg bg-white/70`}>
        <Icon className="h-4.5 w-4.5" />
      </div>
    </div>
    <div className="mt-3 text-xs uppercase tracking-wide text-slate-500">
      {label}
    </div>
    <div className="mt-1 text-2xl font-semibold text-slate-900 tabular-nums">
      {value}
    </div>
    <div className="mt-1 text-xs text-slate-600">{sub}</div>
  </div>
);
